import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutSession } from "../redux/slices/authSlice";
import { getPrimaryNavigation, isNavigationItemActive } from "../config/navigation";
import AccountMenu from "./AccountMenu";
import AssimilationVeins from './ui/AssimilationVeins';
import "./Navbar.css";
import './NavbarField.css';
import logo from "../assets/asslogo1.png";

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const navItems = getPrimaryNavigation(isAuthenticated);

  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const desktop = window.matchMedia?.("(min-width: 1025px)");
    const closeOnDesktop = () => {
      if (desktop?.matches) setIsMenuOpen(false);
    };
    desktop?.addEventListener("change", closeOnDesktop);
    return () => desktop?.removeEventListener("change", closeOnDesktop);
  }, []);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await dispatch(logoutSession());
      closeMenu();
      navigate("/login");
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    document.body.classList.toggle("navbar-menu-open", isMenuOpen);

    const handleKeyDown = (event) => {
      if (!isMenuOpen) return;
      if (event.key === "Escape" && isMenuOpen) {
        event.preventDefault();
        closeMenu();
        toggleRef.current?.focus();
      }
      if (event.key === "Tab") {
        const links = Array.from(menuRef.current?.querySelectorAll("a[href], button:not(:disabled)") || []);
        const first = links[0] || toggleRef.current;
        const last = toggleRef.current;
        const inside = menuRef.current?.contains(document.activeElement) || document.activeElement === last;
        if (!inside || (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault();
          first?.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.classList.remove("navbar-menu-open");
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const timer = window.setTimeout(() => menuRef.current?.querySelector("a")?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [isMenuOpen]);

  return (
    <nav className="navbar" aria-label="Navegação principal">
      <div className="navbar-material" aria-hidden="true">
        <span className="navbar-rupture-interior" />
        <AssimilationVeins className="navbar-veins" />
      </div>
      <div className="navbar-left">
        <Link to="/" className="navbar-logo" onClick={closeMenu} aria-label="Nero Industries - início">
          <span className="navbar-brand-seal" aria-hidden="true"><img src={logo} alt="" className="logo-image" /></span>
          <span className="navbar-wordmark"><strong>Nero Industries</strong><span>Assimilação RPG</span></span>
        </Link>
      </div>

      {isMenuOpen && (
        <button type="button" className="navbar-backdrop" tabIndex={-1} onClick={() => { closeMenu(); toggleRef.current?.focus(); }} aria-label="Fechar navegação" />
      )}

      <div ref={menuRef} id="primary-navigation" className={`navbar-center ${isMenuOpen ? "active" : ""}`}>
        <div className="navbar-index-heading"><span>Caderno de campo</span><strong>Índice</strong><p>Encontre sua próxima história.</p></div>
        {navItems.map((item, index) => {
          const active = isNavigationItemActive(item, location.pathname);
          const Icon = item.icon;
          return (
            <Link
              key={item.id}
              to={item.to}
              className={`navbar-link${active ? " active" : ""}`}
              aria-current={active ? "page" : undefined}
              onClick={closeMenu}
            >
              <span className="navbar-chapter" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
              <span className="navbar-link-label"><Icon className="navbar-link-icon" aria-hidden="true" /><span>{item.label}</span></span>
              {item.badge && <span className="navbar-beta-badge">{item.badge}</span>}
            </Link>
          );
        })}

        <div className="mobile-only-menu">
          <div className="mobile-divider" />
          {user ? (
            <>
              <span className="mobile-user-greeting">Seu registro<strong>{user.name}</strong></span>
              <Link to="/perfil" className="navbar-link" onClick={closeMenu}>Minha conta</Link>
              <Link to="/edit-profile" className="navbar-link" onClick={closeMenu}>Configurações</Link>
              <a href="https://discord.gg/tzrezdyzhs" className="navbar-link" target="_blank" rel="noopener noreferrer" onClick={closeMenu}>Suporte</a>
              <button type="button" onClick={handleLogout} className="mobile-logout-btn" disabled={isLoggingOut}>
                {isLoggingOut ? "Saindo..." : "Sair do sistema"}
              </button>
            </>
          ) : (
            <Link to="/login" className="navbar-link login-btn-mobile" onClick={closeMenu}>
              Acessar conta
            </Link>
          )}
          <span className="navbar-index-note">Nem tudo se perdeu.</span>
        </div>
      </div>

      <button
        ref={toggleRef}
        type="button"
        className={`navbar-toggle ${isMenuOpen ? "open" : ""}`}
        onClick={() => setIsMenuOpen((current) => !current)}
        aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
        aria-expanded={isMenuOpen}
        aria-controls="primary-navigation"
      >
        <span className="navbar-toggle-label">{isMenuOpen ? "Fechar" : "Índice"}</span>
        <span className="navbar-toggle-bars" aria-hidden="true"><span className="bar" /><span className="bar" /><span className="bar" /></span>
      </button>

      <div className="navbar-right desktop-only">
        {user ? (
          <AccountMenu handleLogout={handleLogout} user={user} isLoggingOut={isLoggingOut} />
        ) : (
          <Link to="/login" className="navbar-link login-btn-desktop">Acessar conta<span aria-hidden="true">↗</span></Link>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
